import type {
  MutationOptions,
  QueryObserverOptions,
  UseMutationResult,
  UseQueryResult,
} from '@tanstack/react-query';
import { useMutation, useQuery } from '@tanstack/react-query';

import {
  approveAttachmentsApi,
  deleteAttachmentApi,
  getAttachmentApi,
  getAttachmentFromSignedUrlApi,
  getAttachmentTypesApi,
  getAttachmentUrlApi,
  getViewAttachmentApi,
} from 'api/attachments';
import type {
  ApproveAttachmentsRequestParamsDTO,
  DeleteAttachmentRequestParamsDTO,
  GetAttachmentFromSignedUrlResponseDTO,
  GetAttachmentResponseDTO,
  GetAttachmentUrlResponseDTO,
} from 'declarations/dto/attachments';
import { RequestQueryKeysE } from 'declarations/enums/react-query';
import type { AttachmentTypeI } from 'declarations/interfaces/attachments';
import type { ApiErrorI } from 'declarations/interfaces/errors';

type UseDeleteAttachmentParamsT = {
  onSuccess: MutationOptions<void, ApiErrorI, DeleteAttachmentRequestParamsDTO>['onSuccess'];
};

type UseFetchAttachmentParamsT = {
  ticketId: string;
  fileName: string;
  shouldFetch: boolean;
  onSuccess: QueryObserverOptions<GetAttachmentResponseDTO, ApiErrorI>['onSuccess'];
};

type UseFetchViewAttachmentParamsT = {
  ticketId: string;
  fileName: string;
  shouldFetch: boolean;
  onSuccess: QueryObserverOptions<GetAttachmentResponseDTO, ApiErrorI>['onSuccess'];
};

type UseGetAttachmentUrlParamsT = {
  ticketId: string;
  fileName: string;
  shouldFetch: boolean;
  onSuccess: QueryObserverOptions<GetAttachmentUrlResponseDTO, ApiErrorI>['onSuccess'];
};

type UseDownloadAttachmentFromSignedUrlParamsT = {
  ticketId: string;
  fileName: string;
  shouldFetch: boolean;
  onSuccess: QueryObserverOptions<GetAttachmentFromSignedUrlResponseDTO, ApiErrorI>['onSuccess'];
};

export const useFetchAttachmentForDownload = ({
  ticketId,
  fileName,
  shouldFetch,
  onSuccess,
}: UseFetchAttachmentParamsT): UseQueryResult<GetAttachmentResponseDTO, ApiErrorI> =>
  useQuery<GetAttachmentResponseDTO, ApiErrorI>(
    [RequestQueryKeysE.FetchAttachment, { ticketId, fileName }],
    () => getAttachmentApi(ticketId, fileName),
    {
      enabled: shouldFetch,
      onSuccess,
    }
  );

export const useFetchAttachmentForPreview = ({
  ticketId,
  fileName,
  shouldFetch,
  onSuccess,
}: UseFetchViewAttachmentParamsT): UseQueryResult<GetAttachmentResponseDTO, ApiErrorI> =>
  useQuery<GetAttachmentResponseDTO, ApiErrorI>(
    [RequestQueryKeysE.FetchViewAttachment, { ticketId, fileName }],
    () => getViewAttachmentApi(ticketId, fileName),
    {
      enabled: shouldFetch,
      onSuccess,
    }
  );

export const useDeleteAttachment = ({
  onSuccess,
}: UseDeleteAttachmentParamsT): UseMutationResult<
  void,
  ApiErrorI,
  DeleteAttachmentRequestParamsDTO
> =>
  useMutation<void, ApiErrorI, DeleteAttachmentRequestParamsDTO>(
    [RequestQueryKeysE.DeleteAttachment],
    ({ ticketId, fileName, originalName }) => deleteAttachmentApi(ticketId, fileName, originalName),
    { onSuccess }
  );

export const useGetAttachmentUrl = ({
  ticketId,
  fileName,
  shouldFetch,
  onSuccess,
}: UseGetAttachmentUrlParamsT): UseQueryResult<GetAttachmentUrlResponseDTO, ApiErrorI> =>
  useQuery<GetAttachmentUrlResponseDTO, ApiErrorI>(
    [RequestQueryKeysE.GetAttachmentUrl, { ticketId, fileName }],
    () => getAttachmentUrlApi(ticketId, fileName),
    {
      enabled: shouldFetch,
      onSuccess,
    }
  );

export const useDownloadAttachmentFromSignedUrl = ({
  ticketId,
  fileName,
  shouldFetch,
  onSuccess,
}: UseDownloadAttachmentFromSignedUrlParamsT): UseQueryResult<
  GetAttachmentFromSignedUrlResponseDTO,
  ApiErrorI
> =>
  useQuery<GetAttachmentFromSignedUrlResponseDTO, ApiErrorI>(
    [RequestQueryKeysE.GetAttachmentFromSignedUrl, { ticketId, fileName }],
    () => getAttachmentFromSignedUrlApi(ticketId, fileName),
    {
      enabled: shouldFetch,
      onSuccess,
    }
  );

type UseApproveAttachmentsParamsT = {
  onSuccess: MutationOptions<void, ApiErrorI, ApproveAttachmentsRequestParamsDTO>['onSuccess'];
};

export const useApproveAttachments = ({
  onSuccess,
}: UseApproveAttachmentsParamsT): UseMutationResult<
  void,
  ApiErrorI,
  ApproveAttachmentsRequestParamsDTO
> =>
  useMutation<void, ApiErrorI, ApproveAttachmentsRequestParamsDTO>(
    [RequestQueryKeysE.ApproveAttachments],
    (data): Promise<void> => approveAttachmentsApi(data),
    { onSuccess }
  );

export const useGetAttachmentTypesApi = (): UseQueryResult<AttachmentTypeI[], ApiErrorI> =>
  useQuery<AttachmentTypeI[], ApiErrorI>([RequestQueryKeysE.GetAttachmentTypes], () =>
    getAttachmentTypesApi()
  );
